import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box, Label } from '@apia/theme';
import { useAppSelector } from '../../../../../hooks/hooks';
import store from '../../../../../store';
import propsStore from '../../../../../store/propsStore/propsStore';
import usePropsSelector from '../../../../../store/propsStore/usePropsSelector';
import { remoteModalsActions } from '../../../../../store/remoteModalsSlice';
import { translationActions } from '../../../../../store/translationSlice';
import { hasSucceedFormValidation } from '../../../../../store/validation/util';
import { Confirm } from '../../../../common';
import Form, { TOnValidate } from '../../../../common/validation/Form';
import ApiaComponent from '../../../execution/ApiaComponent';
import fieldsStore from '../../../execution/fieldsStore/fieldsStore';
import {
  ApiaFormContext,
  IApiaFormContext,
} from '../../../execution/page/ApiaForm';
import makeRemoteOpenedModal from '../makeRemoteOpenedModal';
import LanguageList, { ILanguageState } from './LanguagesList';
import { TranslateContext } from './TranlateContext';

import TranslateField from './TranslateField';
import TranslateFilesModal, {
  isFileTranslation,
  ITranslateFilesModal,
} from './TranslateFilesModal';
import { onCancelTranslateModal } from './onCancelTranslateModal';

export interface ILang {
  id: number;
  label: string;
  children: React.ReactNode;
  tooltip: string;
}
export type TTranslateModal = {
  fieldId: string;
  langs: ILang[];
  label: string;
  children?: React.ReactNode | null;
};

const translateModalId = 'translateModal';

const CommonFieldsModal = (data: TTranslateModal) => {
  const browserHeight = window.innerHeight;
  const [ref, setRef] = React.useState<HTMLElement | null>(null);
  const { type, fieldValue, formData, formId } = usePropsSelector(
    data.fieldId,
    (props) => {
      return {
        fieldValue: props.value as string,
        type: props.field.fieldType,
        formData: props.formData,
        formId: props.formData.id,
      };
    },
    shallowEqual,
  );
  const formContext = React.useMemo<IApiaFormContext>(
    () => ({
      formData,
      formId,
      frmId: formData.frmId,
      frmParent: formData.frmParent,
      isFormClosed: false,
      isFormReadOnly: false,
      parsedFormId: formData.frmId,
    }),
    [formData, formId],
  );

  const doFocus = React.useCallback(() => {
    if (!ref) return;
    const translateInput = ref.querySelector(
      '.languageTranslate__translateField',
    ) as HTMLElement;
    if (translateInput) {
      translateInput.focus();
    }
  }, [ref]);

  const translatingId = useAppSelector(
    (global) => global.translationSlice[data.fieldId]?.selectedLanguage?.id,
  );
  const languages = useAppSelector(
    (global) =>
      (global.translationSlice[data.fieldId]?.langs ??
        []) as unknown as ILanguageState[],
    shallowEqual,
  );

  return (
    <Form
      name={`translation${data.fieldId}`}
      unregisterOnUnmount={false}
      onValidate={React.useCallback<TOnValidate<Record<string, string>>>(
        async (res) => {
          if (hasSucceedFormValidation(res)) {
            const entries = Object.entries(res.values);
            for (let index = 0; index < entries.length; index++) {
              const [id, value] = entries[index];
              const sync = async () => {
                if (value === undefined) return;
                const hasSynched = await fieldsStore
                  .getById(data.fieldId)
                  .doSynchronize({
                    forced: true,
                    additionalProps: {
                      langId: id,
                      value,
                      timestamp: Date.now() + index,
                    },
                  });
                if (hasSynched) {
                  store.dispatch(
                    translationActions.setState({
                      id: data.fieldId,
                      selectedLanguage: {
                        id: String(languages[0].id),
                        label: languages[0].label,
                      },
                    }),
                  );
                  store
                    .getState()
                    .translationSlice[data.fieldId].langs?.map((c) => {
                      if (String(c.id) === String(id)) {
                        store.dispatch(
                          translationActions.markAsQueried({
                            id: data.fieldId,
                            langId: String(c.id),
                            hasQueriedServer: false,
                          }),
                        );
                        store.dispatch(
                          translationActions.markIsTemporalyTranslated({
                            id: data.fieldId,
                            langId: String(c.id),
                            isTemporalyTranslated: false,
                          }),
                        );
                        store.dispatch(
                          translationActions.markIsTranslated({
                            id: data.fieldId,
                            langId: String(c.id),
                            isTranslated: !!value,
                          }),
                        );
                        store.dispatch(
                          translationActions.setCurrentValue({
                            id: data.fieldId,
                            langId: String(c.id),
                            currentValue: value,
                          }),
                        );
                      }
                      return c;
                    });
                  fieldsStore.getById(data.fieldId).setError(null);
                }
              };

              await sync();
            }
            store.dispatch(remoteModalsActions.close(translateModalId));
          }
        },
        [data.fieldId, languages],
      )}
    >
      <Confirm onCancel={onCancelTranslateModal}>
        <Box
          className="modalTranslate"
          {...window.getVariant('layout.common.modals.translation')}
        >
          <Box className="modalTranslate__languageSelector">
            <LanguageList
              langs={data?.langs}
              onClick={() => doFocus()}
              fieldId={data.fieldId}
            />
          </Box>

          {
            <Box
              ref={setRef}
              className={`${
                type === 'file'
                  ? 'translateModal__file__content'
                  : 'translateModal__fields__content'
              }
              ${
                type === 'editor' || type === 'area'
                  ? 'translateModal__fields__hasEditor'
                  : ''
              }
              ${browserHeight > 600 ? '' : 'lessHeight'}`}
            >
              <Label
                className={`translateModal__label ${
                  type === 'editor' ? 'translateModal__label__hasEditor' : ''
                } `}
                sx={React.useMemo(
                  () => ({ alignItems: 'start', display: 'block' }),
                  [],
                )}
              >
                <Box className="fieldLabel">{data.label?.trim()}:</Box>
                <Box
                  className={`fieldLabel__container ${
                    type === 'editor' || type === 'area'
                      ? 'fieldLabel__container__hasEditor'
                      : ''
                  } ${
                    browserHeight > 600 && type !== 'input' ? 'hasMore' : ''
                  }`}
                >
                  <Box
                    as="span"
                    sx={React.useMemo(
                      () => ({
                        whiteSpace: 'pre-wrap',
                      }),
                      [],
                    )}
                    className="modalTranslate__languageTranslate__fieldValue"
                    dangerouslySetInnerHTML={{ __html: fieldValue }}
                  />
                </Box>
              </Label>

              {type === 'file' ? (
                <TranslateContext.Provider value={translatingId as string}>
                  <ApiaFormContext.Provider value={formContext}>
                    <ApiaComponent
                      field={{
                        ...propsStore.getFieldProps(data.fieldId).field,
                        id: 'translate',
                      }}
                    />
                  </ApiaFormContext.Provider>
                </TranslateContext.Provider>
              ) : (
                <TranslateField type={type} fieldId={data.fieldId} />
              )}
            </Box>
          }
        </Box>
      </Confirm>
    </Form>
  );
};

const ModalRenderer = ({
  data,
}: {
  data: TTranslateModal | ITranslateFilesModal;
}) => {
  if (isFileTranslation(data)) return <TranslateFilesModal {...data} />;
  return <CommonFieldsModal {...data} />;
};

const { Modal: TranslateModal } = makeRemoteOpenedModal<TTranslateModal>({
  Child: ModalRenderer,

  id: translateModalId,
  onExited() {
    onCancelTranslateModal();
  },
});

export { translateModalId };
export default TranslateModal;
